// Loomis venue management utilities
import { supabase } from '../../../lib/supabase';

export const extractVenueData = (loomisData) => {
  if (!loomisData || loomisData.length < 2) {
    return { uniqueVenues: [], totalLines: 0 };
  }

  const headerRow = loomisData[0];
  const dataRows = loomisData.slice(1);
  
  // Find the kundenummer column (look for "Kundenr")
  const kundenummerIndex = headerRow.findIndex(header => 
    header && header.toString().toLowerCase().includes('kundenr')
  );

  if (kundenummerIndex === -1) {
    throw new Error('Could not find "Kundenr" column in the Loomis report');
  }

  // Extract unique kundenummer values
  const kundenummerSet = new Set();
  dataRows.forEach((row, index) => {
    const kundenummer = row[kundenummerIndex];
    if (kundenummer && kundenummer.toString().trim() !== '') {
      kundenummerSet.add(kundenummer.toString().trim());
    }
  });

  const uniqueVenues = Array.from(kundenummerSet).map(kundenummer => ({
    kundenummer,
    lineCount: dataRows.filter(row => row[kundenummerIndex] === kundenummer).length
  }));

  return {
    uniqueVenues,
    totalLines: dataRows.length,
    kundenummerIndex
  };
};

export const validateVenueMatches = async (loomisData) => {
  try {
    const { uniqueVenues, totalLines } = extractVenueData(loomisData);
    
    if (uniqueVenues.length === 0) {
      return {
        isValid: false,
        error: 'No valid kundenummer values found in the file',
        matchedVenues: [],
        unmatchedVenues: [],
        totalVenues: 0,
        totalDbVenues: 0
      };
    }

    // Get all loomis_venues with their associated venue information
    const { data: loomisVenues, error: loomisVenuesError } = await supabase
      .from('loomis_venues')
      .select(`
        kundenummer,
        venue:venues(id, wolt_venue_name, venue_tripletexid)
      `);

    if (loomisVenuesError) {
      throw new Error(`Failed to fetch loomis venues: ${loomisVenuesError.message}`);
    }

    const matchedVenues = [];
    const unmatchedVenues = [];

    // Check each kundenummer against database
    uniqueVenues.forEach(({ kundenummer, lineCount }) => {
      const matchedLoomisVenue = loomisVenues.find(lv => 
        lv.kundenummer === kundenummer
      );

      if (matchedLoomisVenue && matchedLoomisVenue.venue) {
        matchedVenues.push({
          kundenummer,
          venueName: matchedLoomisVenue.venue.wolt_venue_name,
          venueId: matchedLoomisVenue.venue.id,
          lineCount
        });
      } else {
        unmatchedVenues.push({
          kundenummer,
          lineCount
        });
      }
    });

    return {
      isValid: unmatchedVenues.length === 0,
      matchedVenues,
      unmatchedVenues,
      totalVenues: uniqueVenues.length,
      totalDbVenues: loomisVenues.length,
      totalLines
    };

  } catch (error) {
    return {
      isValid: false,
      error: error.message,
      matchedVenues: [],
      unmatchedVenues: [],
      totalVenues: 0,
      totalDbVenues: 0
    };
  }
};

export const createNewVenue = async (venueData) => {
  try {
    // Build the insert object dynamically based on what's provided
    const insertData = {
      wolt_venue_name: venueData.venueName,
      venue_tripletexid: venueData.tripletexId
    };

    // Add tripletex_number if provided (for Cash Revenue uploads)
    if (venueData.tripletexNumber) {
      insertData.venue_tripletex_number = venueData.tripletexNumber;
    }

    // Optional Vipps ID and Foodora name metadata for the venue
    if (venueData.vippsId) {
      insertData.vipps_id = venueData.vippsId;
    }

    if (venueData.foodoraName) {
      insertData.foodora_name = venueData.foodoraName;
    }

    const { data: createdVenue, error: venueError } = await supabase
      .from('venues')
      .insert([insertData])
      .select()
      .single();

    if (venueError) {
      throw new Error(`Failed to create venue: ${venueError.message}`);
    }

    // If kundenummer is provided, create entry in loomis_venues table
    if (venueData.kundenummer && createdVenue.id) {
      const { error: loomisVenueError } = await supabase
        .from('loomis_venues')
        .insert([{
          venue: createdVenue.id,
          kundenummer: venueData.kundenummer
        }])
        .select()
        .single();

      if (loomisVenueError) {
        console.error('Failed to create loomis_venues entry:', loomisVenueError);
        // Don't fail the whole operation, just log the error
      }
    }

    return { success: true, venue: createdVenue };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const updateVenueKundenummer = async (venueId, kundenummer) => {
  try {
    // Validate inputs
    if (!venueId || !kundenummer) {
      throw new Error('Venue ID and kundenummer are required');
    }

    // Convert venueId to integer if it's a string
    const numericVenueId = typeof venueId === 'string' ? parseInt(venueId, 10) : venueId;
    
    if (isNaN(numericVenueId)) {
      throw new Error(`Invalid venue ID: ${venueId}`);
    }

    console.log('Adding kundenummer to venue:', { venueId: numericVenueId, kundenummer });

    // Check if this kundenummer already exists for this venue
    const { data: existingLoomisVenue, error: checkError } = await supabase
      .from('loomis_venues')
      .select('id')
      .eq('venue', numericVenueId)
      .eq('kundenummer', kundenummer)
      .single();

    if (checkError && checkError.code !== 'PGRST116') { // PGRST116 is "not found" error
      throw new Error(`Failed to check existing kundenummer: ${checkError.message}`);
    }

    if (existingLoomisVenue) {
      return { success: true, message: 'Kundenummer already exists for this venue' };
    }

    // Create new entry in loomis_venues table
    const { data: loomisVenueData, error: loomisVenueError } = await supabase
      .from('loomis_venues')
      .insert([{
        venue: numericVenueId,
        kundenummer: kundenummer
      }])
      .select()
      .single();

    if (loomisVenueError) {
      throw new Error(`Failed to add kundenummer to venue: ${loomisVenueError.message}`);
    }

    // Get the venue information for the response
    const { data: venueData, error: venueError } = await supabase
      .from('venues')
      .select('id, wolt_venue_name, venue_tripletexid, venue_tripletex_number')
      .eq('id', numericVenueId)
      .single();

    if (venueError) {
      console.warn('Failed to fetch venue data:', venueError);
    }

    return { 
      success: true, 
      venue: venueData,
      loomisVenue: loomisVenueData,
      message: 'Kundenummer added successfully'
    };
  } catch (error) {
    console.error('Add kundenummer error:', error);
    return { success: false, error: error.message };
  }
};

export const updateVenueTripletexNumber = async (venueId, tripletexNumber) => {
  try {
    // Validate inputs
    if (!venueId || !tripletexNumber) {
      throw new Error('Venue ID and Tripletex number are required');
    }

    // Convert venueId to integer if it's a string
    const numericVenueId = typeof venueId === 'string' ? parseInt(venueId, 10) : venueId;
    
    if (isNaN(numericVenueId)) {
      throw new Error(`Invalid venue ID: ${venueId}`);
    }

    console.log('Updating venue Tripletex number:', { venueId: numericVenueId, tripletexNumber });

    const { data, error } = await supabase
      .from('venues')
      .update({ venue_tripletex_number: tripletexNumber })
      .eq('id', numericVenueId)
      .select();

    console.log('Update result:', { data, error });

    if (error) {
      throw new Error(`Failed to update venue: ${error.message}`);
    }

    if (!data || data.length === 0) {
      throw new Error(`No venue found with ID: ${numericVenueId}. Please check if the venue still exists.`);
    }

    return { success: true, venue: data[0] };
  } catch (error) {
    console.error('Update venue Tripletex number error:', error);
    return { success: false, error: error.message };
  }
};

export const getAllVenues = async () => {
  try {
    const { data, error } = await supabase
      .from('venues')
      .select(`
        id, 
        wolt_venue_name,
        venue_tripletexid,
        venue_tripletex_number,
        department_name,
        vipps_id,
        foodora_name,
        loomis_venues(kundenummer)
      `)
      .order('wolt_venue_name');

    if (error) {
      throw new Error(`Failed to fetch venues: ${error.message}`);
    }

    // Transform data to include all kundenummers for each venue
    const transformedVenues = data.map(venue => ({
      ...venue,
      kundenummers: venue.loomis_venues ? venue.loomis_venues.map(lv => lv.kundenummer) : [],
      // Keep the first kundenummer for backward compatibility (if any exist)
      venue_loomis_kundenummer: venue.loomis_venues && venue.loomis_venues.length > 0 ? venue.loomis_venues[0].kundenummer : null
    }));

    return { success: true, venues: transformedVenues };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const getVenueKundenummers = async (venueId) => {
  try {
    const { data, error } = await supabase
      .from('loomis_venues')
      .select('kundenummer')
      .eq('venue', venueId)
      .order('kundenummer');

    if (error) {
      throw new Error(`Failed to fetch venue kundenummers: ${error.message}`);
    }

    return { 
      success: true, 
      kundenummers: data.map(lv => lv.kundenummer)
    };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const removeVenueKundenummer = async (venueId, kundenummer) => {
  try {
    const { error } = await supabase
      .from('loomis_venues')
      .delete()
      .eq('venue', venueId)
      .eq('kundenummer', kundenummer);

    if (error) {
      throw new Error(`Failed to remove kundenummer: ${error.message}`);
    }

    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};

export const uploadLoomisData = async (fileName, loomisData, userId, yearMonthMapping = null) => {
  try {
    const { uniqueVenues, kundenummerIndex } = extractVenueData(loomisData);
    
    if (uniqueVenues.length === 0) {
      throw new Error('No valid kundenummer values found in the file');
    }

    // Get all loomis_venues from database for mapping
    const { data: loomisVenues, error: loomisVenuesError } = await supabase
      .from('loomis_venues')
      .select('kundenummer, venue');

    if (loomisVenuesError) {
      throw new Error(`Failed to fetch loomis venues: ${loomisVenuesError.message}`);
    }

    // Create venue mapping
    const venueMapping = {};
    loomisVenues.forEach(loomisVenue => {
      venueMapping[loomisVenue.kundenummer] = loomisVenue.venue;
    });

    // Validate all venues are mapped
    const unmatchedVenues = [];
    uniqueVenues.forEach(({ kundenummer }) => {
      if (!venueMapping[kundenummer]) {
        unmatchedVenues.push(kundenummer);
      }
    });

    if (unmatchedVenues.length > 0) {
      return {
        success: false,
        error: 'Some venues are not matched',
        unmatchedVenues
      };
    }

    // Create upload record
    const { data: uploadRecord, error: uploadError } = await supabase
      .from('loomis_uploads')
      .insert([
        {
          file_name: fileName,
          uploaded_by: userId, // User UUID from auth
          upload_datetime: new Date().toISOString()
        }
      ])
      .select()
      .single();

    if (uploadError) {
      throw new Error(`Failed to create upload record: ${uploadError.message}`);
    }

    if (!uploadRecord || !uploadRecord.id) {
      throw new Error('Upload record was created but no ID was returned');
    }

    console.log('Created upload record:', uploadRecord);

    // Prepare upload lines data
    const dataRows = loomisData.slice(1);
    const uploadLines = [];
    
    dataRows.forEach((row, index) => {
      const kundenummer = row[kundenummerIndex];
      if (kundenummer && kundenummer.toString().trim() !== '') {
        const venueId = venueMapping[kundenummer.toString().trim()];
        
        // Map other columns based on the actual Loomis report structure
        const headerRow = loomisData[0];
        
        // Find Bokføringsdato column (exact match)
        const bokforingsdatoIndex = headerRow.findIndex(header => 
          header && header.toString() === 'Bokføringsdato'
        );
        
        // Find Total column (exact match)
        const totalIndex = headerRow.findIndex(header => 
          header && header.toString() === 'Total'
        );
        
        // Find Bilagsnr. column (exact match)
        const bilagsnrIndex = headerRow.findIndex(header => 
          header && header.toString() === 'Bilagsnr.'
        );
        
        const bokforingsdato = bokforingsdatoIndex !== -1 ? row[bokforingsdatoIndex] : null;
        const belop = totalIndex !== -1 ? row[totalIndex] : null;
        const bilagsnummer = bilagsnrIndex !== -1 ? row[bilagsnrIndex] : null;
        
        // Parse the date properly - handle Excel date serial numbers
        let parsedDate = null;
        if (bokforingsdato) {
          try {
            const dateValue = parseFloat(bokforingsdato);
            console.log('Original date value:', bokforingsdato, 'Parsed number:', dateValue);
            
            // Check if it's an Excel date serial number (typically > 1 and < 100000)
            if (!isNaN(dateValue) && dateValue > 1 && dateValue < 100000) {
              // Excel date serial number conversion
              // Excel's epoch is January 1, 1900, but there's a leap year bug
              // JavaScript's epoch is January 1, 1970
              // So we need to adjust: Excel days since 1900 + days from 1900 to 1970
              const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
              
              // Convert Excel serial number to JavaScript date
              const date = new Date(excelEpoch.getTime() + (dateValue - 2) * 24 * 60 * 60 * 1000);
              
              console.log('Converted date:', date, 'Valid:', !isNaN(date.getTime()), 'Year:', date.getFullYear());
              
              // Check if the date is valid and reasonable
              if (!isNaN(date.getTime()) && date.getFullYear() >= 1900 && date.getFullYear() <= 2100) {
                parsedDate = date.toISOString();
                console.log('Final ISO date:', parsedDate);
              } else {
                console.warn('Date validation failed - invalid converted date');
              }
            } else {
              // Try parsing as regular date string
              const dateStr = bokforingsdato.toString().trim();
              const date = new Date(dateStr);
              
              if (!isNaN(date.getTime()) && date.getFullYear() > 1970) {
                parsedDate = date.toISOString();
                console.log('Parsed as regular date:', parsedDate);
              } else {
                console.warn('Date parsing failed - not a valid Excel serial number or date string');
              }
            }
          } catch (error) {
            console.warn('Failed to parse date:', bokforingsdato, error);
          }
        } else {
          console.log('No bokforingsdato value found');
        }
        
        // Get yearmonth for this line
        const yearmonth = yearMonthMapping && yearMonthMapping[index] ? yearMonthMapping[index] : null;
        
        uploadLines.push({
          venue: venueId,
          bokforingsdato: parsedDate,
          belop: belop ? parseFloat(belop) : null,
          bilagsnummer: bilagsnummer ? bilagsnummer.toString() : null,
          yearmonth: yearmonth,
          upload_id: uploadRecord.id
        });
      }
    });

    // Batch insert upload lines
    if (uploadLines.length > 0) {
      console.log('Inserting upload lines:', uploadLines.length, 'lines with upload_id:', uploadRecord.id);
      
      const { error: linesError } = await supabase
        .from('loomis_upload_lines')
        .insert(uploadLines);

      if (linesError) {
        console.error('Lines insertion error:', linesError);
        
        // Rollback upload record if lines insertion fails
        const { error: deleteError } = await supabase
          .from('loomis_uploads')
          .delete()
          .eq('id', uploadRecord.id);
        
        if (deleteError) {
          console.error('Failed to rollback upload record:', deleteError);
        }
        
        throw new Error(`Failed to insert upload lines: ${linesError.message}`);
      }

      // Insert cash reconciliation entries for Loomis deposits
      console.log('Inserting cash reconciliation entries for Loomis deposits');
      
      // Filter lines that have venue, yearmonth, and positive belop
      const validLines = uploadLines.filter(line => 
        line.venue && line.yearmonth && line.belop && line.belop > 0
      );

      if (validLines.length > 0) {
        console.log('Processing cash reconciliation entries:', validLines.length);
        
        // Group lines by venue and yearmonth to batch insert
        const groupedLines = validLines.reduce((acc, line) => {
          const key = `${line.venue}-${line.yearmonth}`;
          if (!acc[key]) {
            acc[key] = {
              venue: line.venue,
              yearmonth: line.yearmonth,
              lines: []
            };
          }
          acc[key].lines.push(line);
          return acc;
        }, {});

        // For each venue/yearmonth group, create or find reconciliation and insert lines
        for (const group of Object.values(groupedLines)) {
          try {
            // Parse yearmonth (format: YYYY-MM)
            const [year, month] = group.yearmonth.split('-').map(Number);

            // Find or create parent reconciliation
            let { data: existingReconciliation, error: findError } = await supabase
              .from('cash_reconciliations')
              .select('id')
              .eq('venue', group.venue)
              .eq('year', year)
              .eq('month', month)
              .single();

            let reconciliationId;

            if (findError && findError.code === 'PGRST116') {
              // No reconciliation exists, create one
              const { data: newReconciliation, error: createError } = await supabase
                .from('cash_reconciliations')
                .insert([{
                  venue: group.venue,
                  year: year,
                  month: month
                }])
                .select()
                .single();

              if (createError) {
                console.error('Failed to create reconciliation:', createError);
                continue; // Skip this group but continue with others
              }

              reconciliationId = newReconciliation.id;
            } else if (findError) {
              console.error('Failed to find reconciliation:', findError);
              continue; // Skip this group but continue with others
            } else {
              reconciliationId = existingReconciliation.id;
            }

            // Insert all lines for this reconciliation
            const linesToInsert = group.lines.map(line => ({
              cash_reconciliation: reconciliationId,
              event_type: 'Loomis deposit',
              amount: Math.abs(line.belop) // Ensure positive amount for deposits
            }));

            const { error: lineError } = await supabase
              .from('cash_reconciliation_lines')
              .insert(linesToInsert);

            if (lineError) {
              console.error('Failed to insert reconciliation lines:', lineError);
              // Continue with other groups
            }
          } catch (error) {
            console.error('Error processing reconciliation group:', error);
            // Continue with other groups
          }
        }
      }
    }

    return {
      success: true,
      uploadId: uploadRecord.id,
      fileName: uploadRecord.file_name,
      totalLines: uploadLines.length,
      matchedVenues: uniqueVenues.length
    };

  } catch (error) {
    console.error('Upload error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
